import React, { useState, useEffect } from "react";
import { History, Download, Eye, FileText, Calendar, User, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-toastify";
import axiosInstance from "../../../common/axiosInstance";
import { S3TempUrl } from "../../../utils/s3Client";
import { DesignDocument, DocumentVersion } from "./DesignDocumentationIntegration";
import Modal from "../../../components/modal/Modal";
import { SpinLoader } from "../../../components/loader/Loader";

interface DocumentVersionHistoryProps {
  isOpen: boolean;
  onClose: () => void;
  document: DesignDocument;
  onViewVersion: (document: DesignDocument, version: DocumentVersion) => void;
}

const DocumentVersionHistory: React.FC<DocumentVersionHistoryProps> = ({
  isOpen,
  onClose,
  document,
  onViewVersion,
}) => {
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedVersions, setExpandedVersions] = useState<Set<number>>(new Set());
  const { getsignedurl } = S3TempUrl();

  useEffect(() => {
    if (isOpen) {
      fetchVersions();
    }
  }, [isOpen, document]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const docId = document._id || document.id;
      const folderId = (document as any).folderId;
      
      if (!docId || !folderId) {
        // Fallback to document's versions if IDs not available
        if (document.versions && document.versions.length > 0) {
          setVersions(document.versions);
        } else {
          setVersions([
            {
              version: document.currentVersion || 1,
              fileUrl: document.fileUrl,
              fileName: document.fileName,
              uploadedAt: document.uploadedAt,
              uploadedBy: document.uploadedBy,
              fileSize: document.fileSize,
            },
          ]);
        }
        return;
      }

      const response = await axiosInstance.get(
        `/api/utility-systems/design-folders/${folderId}/documents/${docId}/versions`
      );

      if (response.data.success && response.data.resCode === 200) {
        const fetchedVersions = response.data.data || response.data.message || [];
        
        // If no versions from API, use document's versions or create initial version
        if (Array.isArray(fetchedVersions) && fetchedVersions.length > 0) {
          setVersions(fetchedVersions);
        } else if (document.versions && document.versions.length > 0) {
          setVersions(document.versions);
        } else {
          // Create initial version from document data
          setVersions([
            {
              version: document.currentVersion || 1,
              fileUrl: document.fileUrl,
              fileName: document.fileName,
              uploadedAt: document.uploadedAt,
              uploadedBy: document.uploadedBy,
              fileSize: document.fileSize,
            },
          ]);
        }
      } else {
        // Fallback to document's versions
        if (document.versions && document.versions.length > 0) {
          setVersions(document.versions);
        } else {
          setVersions([
            {
              version: document.currentVersion || 1,
              fileUrl: document.fileUrl,
              fileName: document.fileName,
              uploadedAt: document.uploadedAt,
              uploadedBy: document.uploadedBy,
              fileSize: document.fileSize,
            },
          ]);
        }
      }
    } catch (error: any) {
      console.error("Error fetching versions:", error);
      // Fallback to document's versions
      if (document.versions && document.versions.length > 0) {
        setVersions(document.versions);
      } else {
        setVersions([
          {
            version: document.currentVersion || 1,
            fileUrl: document.fileUrl,
            fileName: document.fileName,
            uploadedAt: document.uploadedAt,
            uploadedBy: document.uploadedBy,
            fileSize: document.fileSize,
          },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (version: DocumentVersion) => {
    try {
      // Use signedUrl from backend if available, otherwise generate one
      let fileUrl = (version as any).signedUrl;
      if (!fileUrl) {
        fileUrl = await getsignedurl(version.fileName, version.fileUrl);
      }
      const link = window.document.createElement("a");
      link.href = fileUrl;
      link.download = version.fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      toast.success("Download started");
    } catch (error) {
      console.error("Error downloading version:", error);
      toast.error("Failed to download version");
    }
  };

  const toggleVersionExpansion = (versionNumber: number) => {
    setExpandedVersions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(versionNumber)) {
        newSet.delete(versionNumber);
      } else {
        newSet.add(versionNumber);
      }
      return newSet;
    });
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return "Unknown";
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // Sort versions by version number (descending)
  const sortedVersions = [...versions].sort((a, b) => b.version - a.version);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      modalClass="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative"
    >
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Version History - {document.documentName}
        </h2>
      </div>
      <div className="space-y-4">
        {/* Document Info */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-3">
            <FileText className="w-6 h-6 text-blue-600 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">{document.documentName}</h3>
              <p className="text-sm text-gray-600 mt-1">
                {document.description || "No description"}
              </p>
              <div className="flex gap-4 mt-2 text-xs text-gray-500">
                <span>Type: {document.documentType.replace("_", " ").toUpperCase()}</span>
                <span>Current Version: v{document.currentVersion || 1}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Versions List */}
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <SpinLoader />
          </div>
        ) : sortedVersions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No version history available</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedVersions.map((version, index) => {
              const isExpanded = expandedVersions.has(version.version);
              const isCurrentVersion = version.version === document.currentVersion;

              return (
                <div
                  key={version.version}
                  className={`border rounded-lg transition-all ${
                    isCurrentVersion
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  {/* Version Header */}
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => toggleVersionExpansion(version.version)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                            isCurrentVersion
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200 text-gray-700"
                          }`}
                        >
                          v{version.version}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">
                              Version {version.version}
                            </span>
                            {isCurrentVersion && (
                              <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(version.uploadedAt)}</span>
                            </div>
                            {version.uploadedBy && (
                              <div className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                <span>{version.uploadedBy}</span>
                              </div>
                            )}
                            <span className="text-gray-400">•</span>
                            <span>{formatFileSize(version.fileSize)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewVersion(document, version);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title="View"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(version);
                          }}
                          className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-200 pt-4">
                      <div className="space-y-2">
                        <div>
                          <span className="text-sm font-medium text-gray-700">File Name:</span>
                          <p className="text-sm text-gray-600 mt-1">{version.fileName}</p>
                        </div>
                        {version.changeDescription && (
                          <div>
                            <span className="text-sm font-medium text-gray-700">
                              Change Description:
                            </span>
                            <p className="text-sm text-gray-600 mt-1">
                              {version.changeDescription}
                            </p>
                          </div>
                        )}
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => onViewVersion(document, version)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </button>
                          <button
                            onClick={() => handleDownload(version)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Summary */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Total Versions: {versions.length}</span>
            <span>Current Version: v{document.currentVersion || 1}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DocumentVersionHistory;

